// Datos del clima de la obra: Open-Meteo (gratis, sin clave). Pronóstico, días pasados, archivo histórico y
// climatología de 10 años. Todo con caché (memoria + localStorage) para no repetir pedidos: Open-Meteo pide
// no pasar de ~10.000 llamadas por día por IP y un mes cerrado no cambia más.
import R from './reglasClima';

const HOURLY_FC = 'temperature_2m,apparent_temperature,precipitation_probability,precipitation,weather_code,wind_speed_10m,wind_gusts_10m,wind_direction_10m,relative_humidity_2m,dew_point_2m,visibility,uv_index,is_day,pressure_msl';
const HOURLY_AR = 'temperature_2m,apparent_temperature,precipitation,weather_code,wind_speed_10m,wind_gusts_10m,wind_direction_10m,relative_humidity_2m,dew_point_2m';
const DAILY_FC = 'weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,precipitation_probability_max,precipitation_hours,wind_speed_10m_max,wind_gusts_10m_max,wind_direction_10m_dominant,uv_index_max,sunrise,sunset';
const DAILY_AR = 'weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,precipitation_hours,wind_speed_10m_max,wind_gusts_10m_max,wind_direction_10m_dominant,sunrise,sunset';
const CURRENT = 'temperature_2m,apparent_temperature,relative_humidity_2m,precipitation,rain,weather_code,cloud_cover,pressure_msl,wind_speed_10m,wind_direction_10m,wind_gusts_10m,is_day,visibility,uv_index';

const mem = new Map();
const LS = 'clima:v1:';
const lsGet = (k, maxAgeMs) => {
  try {
    const x = JSON.parse(localStorage.getItem(LS + k) || 'null');
    if (x && (!maxAgeMs || Date.now() - x.at < maxAgeMs)) return x.v;
  } catch (e) { /* sin storage */ }
  return null;
};
const lsSet = (k, v) => {
  const put = () => localStorage.setItem(LS + k, JSON.stringify({ at: Date.now(), v }));
  try { put(); } catch (e) {
    // lleno: se borran las entradas más viejas de este módulo y se reintenta una vez
    try {
      const keys = Object.keys(localStorage).filter((x) => x.startsWith(LS)).map((x) => [x, (JSON.parse(localStorage.getItem(x)) || {}).at || 0]).sort((a, b) => a[1] - b[1]);
      keys.slice(0, Math.ceil(keys.length / 2)).forEach(([x]) => localStorage.removeItem(x));
      put();
    } catch (e2) { /* sin lugar: queda solo en memoria */ }
  }
};
const lugar = (loc) => `${(+loc.lat).toFixed(3)},${(+loc.lng).toFixed(3)}`;

// Open-Meteo a veces responde 429/5xx, o un 200 con texto ("Unexpected error while streaming data:
// timeoutReached") en vez de JSON. Se reintenta solo (3 intentos) y, si no, un error amable: nunca el texto
// técnico del servicio en pantalla.
const MSG_CLIMA = 'El servicio del clima está tardando en responder. Lo volvemos a intentar solos en unos segundos.';
const errorAmable = (msg = MSG_CLIMA) => Object.assign(new Error(msg), { amigable: true });
export const mensajeClima = (e) => (e && e.amigable ? e.message : MSG_CLIMA);

async function get(url, intento = 0) {
  let j = null;
  try {
    const r = await fetch(url);
    const txt = await r.text();
    if (r.ok) { try { j = JSON.parse(txt); } catch (e) { j = null; } }
    if (j && !j.error) return j;
  } catch (e) { /* red caída: se reintenta */ }
  if (intento < 2) { await new Promise((ok) => setTimeout(ok, intento ? 4000 : 1500)); return get(url, intento + 1); }
  throw errorAmable();
}
function cached(key, ttlMs, fn, { persist = true } = {}) {
  const hit = mem.get(key);
  if (hit && Date.now() - hit.at < ttlMs) return hit.p;
  if (persist) {
    const v = lsGet(key, ttlMs);
    if (v) { const p = Promise.resolve(v); mem.set(key, { at: Date.now(), p }); return p; }
  }
  const p = fn().then((v) => { if (persist) lsSet(key, v); return v; });
  mem.set(key, { at: Date.now(), p });
  p.catch(() => mem.delete(key));
  return p;
}

/** Ahora + 48 h + 16 días (con 2 días pasados para las ventanas de lluvia y minutely_15 para "lloviendo ahora"). */
export function pronostico(loc) {
  return cached('fc:' + lugar(loc), 15 * 60e3, async () => {
    const j = await get(`https://api.open-meteo.com/v1/forecast?latitude=${loc.lat}&longitude=${loc.lng}&current=${CURRENT}&hourly=${HOURLY_FC}&daily=${DAILY_FC}&minutely_15=precipitation&past_minutely_15=8&forecast_minutely_15=8&past_days=2&forecast_days=16&timezone=auto`);
    return { ...R.desdeOpenMeteo(j), elevacion: j.elevation, zona: j.timezone, traido: Date.now() };
  }, { persist: false });
}

/** Hoy en la zona de la obra ("YYYY-MM-DD"), según el desfase que informó Open-Meteo. */
export const hoyObra = (offset) => R.localDe(Date.now(), offset).slice(0, 10);

// Un mes de datos pasados (horario + diario). Los meses cerrados quedan guardados para siempre (90 días de TTL).
function mesPasado(loc, ym, hoy) {
  const desde = `${ym}-01`;
  const ultimo = new Date(Date.UTC(+ym.slice(0, 4), +ym.slice(5, 7), 0)).toISOString().slice(0, 10);
  const ayer = R.addDias(hoy, -1);
  const hasta = ultimo < ayer ? ultimo : ayer;
  if (hasta < desde) return Promise.resolve({ horas: [], dias: [], offset: 0 });
  const cerrado = ultimo < R.addDias(hoy, -6);
  return cached(`mes:${lugar(loc)}:${ym}:${cerrado ? 'c' : hasta}`, cerrado ? 90 * 864e5 : 3 * 3600e3, async () => {
    // Lo de los últimos ~3 meses sale del pronóstico con días pasados (el archivo tarda ~5 días en llenarse)
    const reciente = desde >= R.addDias(hoy, -88);
    const url = reciente
      ? `https://api.open-meteo.com/v1/forecast?latitude=${loc.lat}&longitude=${loc.lng}&hourly=${HOURLY_AR}&daily=${DAILY_AR}&start_date=${desde}&end_date=${hasta}&timezone=auto`
      : `https://archive-api.open-meteo.com/v1/archive?latitude=${loc.lat}&longitude=${loc.lng}&hourly=${HOURLY_AR}&daily=${DAILY_AR}&start_date=${desde}&end_date=${hasta}&timezone=auto`;
    const p = R.desdeOpenMeteo(await get(url));
    // se guarda compacto (sin campos vacíos) para no llenar el localStorage
    const h = p.horas.map((x) => [x.t, x.temp, x.sens, x.mm, x.code, x.viento, x.rafaga, x.dir, x.hum, x.rocio]);
    return { offset: p.offset, h, dias: p.dias };
  }, { persist: true }).then((c) => (c.h ? { offset: c.offset, dias: c.dias, horas: c.h.map((a) => ({ t: a[0], fecha: a[0].slice(0, 10), h: +a[0].slice(11, 13), temp: a[1], sens: a[2], mm: a[3] || 0, code: a[4], viento: a[5], rafaga: a[6], dir: a[7], hum: a[8], rocio: a[9], prob: null })) } : c));
}

/** Datos pasados de un rango (días cerrados). Devuelve { horas, dias, offset }. */
export async function historico(loc, desde, hasta, hoy) {
  const meses = [];
  for (let m = desde.slice(0, 7); m <= hasta.slice(0, 7); m = R.addDias(`${m}-28`, 7).slice(0, 7)) meses.push(m);
  const partes = await Promise.all(meses.map((m) => mesPasado(loc, m, hoy)));
  const horas = [], dias = [];
  partes.forEach((p) => { p.horas.forEach((x) => { if (x.fecha >= desde && x.fecha <= hasta) horas.push(x); }); p.dias.forEach((d) => { if (d.fecha >= desde && d.fecha <= hasta) dias.push(d); }); });
  return { horas, dias, offset: (partes[0] && partes[0].offset) || 0 };
}

/** 10 años de datos diarios del lugar (para "lo que suele pasar" en cada época). Se guarda 30 días. */
export function diezAnios(loc, hoy) {
  const y = +hoy.slice(0, 4);
  return cached(`clim:${(+loc.lat).toFixed(2)},${(+loc.lng).toFixed(2)}:${y}`, 30 * 864e5, async () => {
    const j = await get(`https://archive-api.open-meteo.com/v1/archive?latitude=${loc.lat}&longitude=${loc.lng}&start_date=${y - 10}-01-01&end_date=${y - 1}-12-31&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,wind_gusts_10m_max&timezone=auto`);
    const d = j.daily || {};
    return (d.time || []).map((f, i) => [f, d.precipitation_sum[i], d.temperature_2m_max[i], d.temperature_2m_min[i], d.wind_gusts_10m_max[i], d.weather_code[i]]);
  }).then((rows) => rows.filter((r) => r[2] != null).map((r) => ({ fecha: r[0], mm: r[1] || 0, tmax: r[2], tmin: r[3], rafaga: r[4], code: r[5] })));
}

/** Junta varias fuentes (pasado + pronóstico) en un índice; lo más nuevo pisa a lo viejo. */
export function unir(...fuentes) {
  const horas = new Map(), dias = new Map();
  let offset = 0;
  fuentes.filter(Boolean).forEach((f) => {
    if (f.offset) offset = f.offset;
    (f.horas || []).forEach((x) => horas.set(x.t, x));
    (f.dias || []).forEach((d) => dias.set(d.fecha, d));
  });
  const p = { offset, horas: [...horas.values()].sort((a, b) => (a.t < b.t ? -1 : 1)), dias: [...dias.values()].sort((a, b) => (a.fecha < b.fecha ? -1 : 1)) };
  return R.indexar(p);
}
